import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const Visiting = () => {
  return (
    <>
      <Helmet>
        <title>Plan Your Visit - Shree Ram Ranuja Dham Ashram | Rampar, Gujarat</title>
        <meta name="description" content="Everything you need to know for a meaningful visit to Shree Ram Ranuja Dham Ashram in Rampar, Lathi, Amreli, Gujarat — temple hours, directions, parking, facilities and guidelines." />
        <link rel="canonical" href="https://www.shreeramranujadham.org/visiting" />
        <meta property="og:title" content="Plan Your Visit - Shree Ram Ranuja Dham Ashram | Rampar, Gujarat" />
        <meta property="og:description" content="Everything you need to know for a meaningful visit to Shree Ram Ranuja Dham Ashram in Rampar, Lathi, Amreli, Gujarat." />
        <meta property="og:url" content="https://www.shreeramranujadham.org/visiting" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
      </Helmet>

      {/* ── Page Header ─────────────────────────────────── */}
      <section
        className="pt-24 pb-16 relative overflow-hidden lotus-pattern"
        style={{ background: 'linear-gradient(to right, #16a34a, #f97316, #9333ea)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-6">
            <span className="inline-block bg-white bg-opacity-20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚗 Plan Your Sacred Visit
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
            Visiting Guidelines &<br />
            <span className="text-yellow-300">Temple Schedule</span>
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed mb-4">
            Everything you need to know to make your visit to our sacred sanctuary peaceful, meaningful, and divine.
          </p>
        </div>
      </section>

      {/* ── Guidelines Grid Section ───────────────────── */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Temple Hours */}
            <div className="temple-card p-6 rounded-xl text-center hover:shadow-xl transition-all duration-300 border border-gray-100 shadow-md">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow">
                <i className="fas fa-clock text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Temple Hours</h3>
              <p className="text-sm text-gray-600">Morning: 5:30 AM - 12:00 PM</p>
              <p className="text-sm text-gray-600">Evening: 4:00 PM - 9:00 PM</p>
            </div>

            {/* Free Entry */}
            <div className="temple-card p-6 rounded-xl text-center hover:shadow-xl transition-all duration-300 border border-gray-100 shadow-md">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow">
                <i className="fas fa-gift text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Free Entry</h3>
              <p className="text-sm text-gray-600">No entry fees</p>
              <p className="text-sm text-gray-600">All are welcome</p>
            </div>

            {/* Parking */}
            <div className="temple-card p-6 rounded-xl text-center hover:shadow-xl transition-all duration-300 border border-gray-100 shadow-md">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow">
                <i className="fas fa-car text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Free Parking</h3>
              <p className="text-sm text-gray-600">Ample parking space</p>
              <p className="text-sm text-gray-600">Security available</p>
            </div>

            {/* Facilities */}
            <div className="temple-card p-6 rounded-xl text-center hover:shadow-xl transition-all duration-300 border border-gray-100 shadow-md">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow">
                <i className="fas fa-wheelchair text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Accessible</h3>
              <p className="text-sm text-gray-600">Wheelchair accessible</p>
              <p className="text-sm text-gray-600">Family-friendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Temple Hours & Schedule Section ───────────── */}
      <section id="visiting-hours" className="py-20 bg-gradient-to-b from-orange-50 to-white temple-pattern">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Temple Hours & Daily Schedule</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plan your visit around our daily spiritual activities and special programs
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Daily Schedule */}
            <div className="temple-card p-8 rounded-xl shadow-lg bg-white border border-orange-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <i className="fas fa-calendar-day text-orange-500 mr-3 text-2xl"></i>
                Daily Schedule
              </h3>
              <div className="space-y-4 font-poppins">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Morning Aarti</h4>
                    <p className="text-xs text-gray-500">Start your day with divine blessings</p>
                  </div>
                  <span className="text-orange-600 font-bold">6:00 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Meditation Session</h4>
                    <p className="text-xs text-gray-500">Guided meditation and pranayama</p>
                  </div>
                  <span className="text-orange-600 font-bold">7:00 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Scripture Reading</h4>
                    <p className="text-xs text-gray-500">Daily reading from sacred texts</p>
                  </div>
                  <span className="text-orange-600 font-bold">8:00 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Evening Aarti</h4>
                    <p className="text-xs text-gray-500">Divine prayers and devotional songs</p>
                  </div>
                  <span className="text-orange-600 font-bold">7:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">Night Prayers</h4>
                    <p className="text-xs text-gray-500">Peaceful conclusion to the day</p>
                  </div>
                  <span className="text-orange-600 font-bold">8:30 PM</span>
                </div>
              </div>
            </div>

            {/* Special Programs */}
            <div className="temple-card p-8 rounded-xl shadow-lg bg-white border border-orange-100">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <i className="fas fa-star text-orange-500 mr-3 text-2xl"></i>
                Special Programs
              </h3>
              <div className="space-y-4 font-poppins">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Sunday Events</h4>
                    <p className="text-xs text-gray-500">Weekly community gathering</p>
                  </div>
                  <span className="text-purple-600 font-bold">9:00 AM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Wednesday Bhajan</h4>
                    <p className="text-xs text-gray-500">Devotional music session</p>
                  </div>
                  <span className="text-purple-600 font-bold">7:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <div>
                    <h4 className="font-semibold text-gray-800">Friday Discourse</h4>
                    <p className="text-xs text-gray-500">Guruji's spiritual teachings</p>
                  </div>
                  <span className="text-purple-600 font-bold">6:30 PM</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <div>
                    <h4 className="font-semibold text-gray-800">Monthly Seva</h4>
                    <p className="text-xs text-gray-500">Community service activities</p>
                  </div>
                  <span className="text-purple-600 font-bold">Last Sunday</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Directions & Location Section ──────────────── */}
      <section id="directions" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Location & Directions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find us easily with detailed directions and transportation options
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            {/* Map iframe */}
            <div className="temple-card rounded-xl overflow-hidden shadow-lg border border-gray-200">
              <div className="bg-gray-200 h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.845!2d71.4554605!3d21.7322707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39589b17833f5173%3A0x33324c124b6534af!2sShree%20Ram%20Ranuja%20Dham%20Ashram!5e0!3m2!1sen!2sin!4v1726584000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Ashram Map Location"
                ></iframe>
              </div>
            </div>

            {/* Details and Transport info */}
            <div className="space-y-8">
              {/* Address card */}
              <div className="temple-card p-6 rounded-xl shadow-md border border-gray-100 bg-white">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <i className="fas fa-map-marker-alt text-orange-500 mr-3 text-xl"></i>
                  Temple Address
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                  Shree Ram Ranuja Dham Ashram<br />
                  Lathi–Bhurkhiya Road, Rampar<br />
                  Taluka: Lathi, District: Amreli<br />
                  Gujarat – 365430, India
                </p>
                <div className="mt-5">
                  <a
                    href="https://www.google.com/maps/place/Shree+Ram+Ranuja+Dham+Ashram/data=!4m2!3m1!1s0x0:0x33324c124b6534af"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-2.5 rounded-xl shadow transition-all duration-300"
                  >
                    <i className="fas fa-directions mr-2"></i>Get Directions
                  </a>
                </div>
              </div>

              {/* Transportation card */}
              <div className="temple-card p-6 rounded-xl shadow-md border border-gray-100 bg-white">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <i className="fas fa-route text-orange-500 mr-3 text-xl"></i>
                  How to Reach
                </h3>
                <div className="space-y-3.5 text-gray-700 text-sm md:text-base leading-relaxed">
                  <p><strong>By Car:</strong> Take Highway 123, exit at Sacred City. Follow signs to Spiritual Path.</p>
                  <p><strong>By Train:</strong> Sacred City Railway Station (2 km). Auto-rickshaws and taxis available.</p>
                  <p><strong>By Bus:</strong> City Bus Route 45 stops directly at the temple.</p>
                  <p><strong>By Air:</strong> Sacred City Airport (15 km). Taxi and bus services available.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-r from-orange-500 via-red-500 to-purple-600 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Visit?</h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Come experience the divine peace and spiritual blessings at Shree Ram Ranuja Dham
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-orange-600 px-10 py-4 rounded-2xl font-bold text-lg hover:bg-orange-50 transition-all shadow-2xl hover:scale-105"
          >
            <i className="fas fa-phone mr-3"></i>Contact Us
            <i className="fas fa-arrow-right ml-3"></i>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Visiting;
